import React, { useEffect, useState } from 'react';
import { Form, Modal, Button, Switch, Select, DatePicker, Input, Tooltip, InputNumber, notification, Spin } from 'antd';
import { convertDateToFormat, handleFormChange, layoutEdit, validateMessages } from '../../../shared/Utils';
import { TaxiDriver } from '../../../shared/types';
import { useCurrentRegisterTaxiDriver } from '../hooks/useCurrentRegisterTaxiDriver';
import { useUpdateStatusDriverMutation } from '../../../GraphQL/hooks/useUpdateStatusDriverMutation';
import { allRegisterStatusTypes, allAccountStatusTypes } from '../../../shared/types';
import dayjs from 'dayjs';
import { RedoOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';

const RegisterTaxiDriverEditModal = ({ isOpen, onRequestClose, updateList }) => {
    const [form] = Form.useForm();
    const [formError, setFormError] = useState(false);
    const [newTaxiDriver, setNewTaxiDriver] = useState<TaxiDriver>();
    const { currentRegisterTaxiDriver } = useCurrentRegisterTaxiDriver();
    const handleSwitchAccountStatusChange = (value: string) => {
        setNewTaxiDriver({ ...newTaxiDriver, accountStatus: value });
    };

    const handleSwitchRegisterStatusChange = (value: string) => {
        setNewTaxiDriver({ ...newTaxiDriver, registerStatus: value });
    };

    const handleSelectExpiredInsuranceDate = (date: any, dateString: any) => {
        setNewTaxiDriver({ ...newTaxiDriver, expiredInsuranceDate: convertDateToFormat(date, 'DD-MM-YYYY') });
    };

    const handleSelectExpiredAccountDate = (date: any, dateString: any) => {
        setNewTaxiDriver({ ...newTaxiDriver, expiredAccountDate: convertDateToFormat(date, 'DD-MM-YYYY') });
    };

    const handleSwitchChange = (checked: boolean) => {
        setNewTaxiDriver((prev) => ({
            ...prev,
            isRewardedReferral: checked,
        }));
    };
    const { mutation, loading: creating } = useUpdateStatusDriverMutation();

    useEffect(() => {
        if (currentRegisterTaxiDriver) {
            const { password, ...sanitizedRegisterTaxiDriver } = currentRegisterTaxiDriver;
            form.setFieldsValue({
                ...sanitizedRegisterTaxiDriver,
                expiredInsuranceDate: dayjs(Number(currentRegisterTaxiDriver?.expiredInsuranceDate)),
                expiredAccountDate: dayjs(Number(currentRegisterTaxiDriver?.expiredAccountDate)),
            });
        }
    }, [form, currentRegisterTaxiDriver]);

    const WORK_RADIUS_OPTIONS = [
        { key: '1', range: 5, commissionRate: 15, monthlyFee: 49, note: '' },
        { key: '2', range: 10, commissionRate: 10, monthlyFee: 62, note: '' },
        { key: '3', range: 15, commissionRate: 5, monthlyFee: 79, note: '' },
        { key: '4', range: 10, commissionRate: 0, monthlyFee: 0, note: 'Trial' },
    ];

    const handleRadiusChange = (key: string) => {
        const plan = WORK_RADIUS_OPTIONS.find((option) => option.key === key);

        if (!plan) return;

        form.setFieldsValue({
            workRangeKm: plan.range,
            monthlyFee: plan.monthlyFee,
            commissionRate: plan.commissionRate,
        });
    };

    const handleCommit = async () => {
        try {
            const values = await form.validateFields();
            if (values) {
                await mutation({
                    _id: currentRegisterTaxiDriver._id,
                    ...values,
                })
                    .then(() => {
                        onRequestClose(false);
                        updateList();
                    })
                    .finally(() => {
                        notification.success({
                            message: 'Driver updated successfully',
                            description: 'The driver information has been updated successfully.',
                        });
                    })
                    .catch((err) => {
                        notification.error({
                            message: 'Failed to update driver information',
                            description: 'An error occurred while updating the driver information.',
                        });

                        console.error('Failed to update driver information:', err);
                    });
            } else {
                notification.error({
                    message: 'Form validation failed',
                    description: 'Please check the form for errors and try again.',
                });
            }
        } catch (errorInfo) {
            notification.error({
                message: 'Failed to update driver information',
                description: 'An error occurred while updating the driver information.',
            });
            console.error('Form validation failed:', errorInfo);
        }
    };

    const generatePassword = () => {
        const newPassword = uuidv4().slice(0, 10);
        form.setFieldsValue({ password: newPassword });
    };

    const handleCancel = () => {
        onRequestClose(false);
    };

    return (
        <Modal
            title="Check Driver info"
            open={isOpen}
            destroyOnClose={true}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="edit" type="primary" onClick={handleCommit} disabled={formError}>
                    Edit
                </Button>,
            ]}
        >
            <Spin spinning={creating}>
                <Form<TaxiDriver>
                    {...layoutEdit}
                    form={form}
                    name="nest-messages"
                    onFieldsChange={() => {
                        setFormError(handleFormChange(form));
                    }}
                    onValuesChange={(changedValues, allValues) => setNewTaxiDriver(allValues)}
                    validateMessages={validateMessages}
                >
                    <Form.Item
                        name="fullName"
                        label="Full name"
                        rules={[
                            { message: 'Please provide a valid FullName!' },
                            { required: true, message: 'Please enter the FullName!' },
                        ]}
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <Input type="text" />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { type: 'email', message: 'Please provide a valid email!' },
                            { required: true, message: 'Please enter E-mail!' },
                        ]}
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <Input type="email" />
                    </Form.Item>
                    <Form.Item
                        name="phoneNumber"
                        label="Phone Number"
                        rules={[{ required: true, message: 'Please enter Phone Number!' }]}
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <Input type="tel" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="New password"
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <Input.Password
                            type="password"
                            autoComplete="new-password"
                            addonAfter={
                                <Tooltip title="Generate new password" color={'#108ee9'}>
                                    <RedoOutlined style={{ color: '#1677ff' }} onClick={generatePassword} />
                                </Tooltip>
                            }
                        />
                    </Form.Item>
                    <Form.Item
                        name="marque"
                        label="Marque"
                        rules={[{ required: true, message: 'Please enter marque of vehicle' }]}
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <Input placeholder="e.g. Toyota" />
                    </Form.Item>
                    <Form.Item
                        name="model"
                        label="Model"
                        rules={[{ required: true, message: 'Please enter model of vehicle!' }]}
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <Input placeholder="e.g. Prius" />
                    </Form.Item>
                    <Form.Item
                        name="accountStatus"
                        label="Account status"
                        rules={[{ required: true }]}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        style={{ display: 'inline-block', width: '80%' }}
                    >
                        <Select
                            placeholder="Select account status"
                            value={currentRegisterTaxiDriver?.accountStatus}
                            onChange={handleSwitchAccountStatusChange}
                        >
                            {(allAccountStatusTypes ?? []).map((type) => (
                                <Select.Option key={type} value={type}>
                                    {type}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="registerStatus"
                        label="Register status"
                        rules={[{ required: true }]}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        style={{ display: 'inline-block', width: '80%' }}
                    >
                        <Select
                            placeholder="Select register status"
                            value={currentRegisterTaxiDriver?.registerStatus}
                            onChange={handleSwitchRegisterStatusChange}
                        >
                            {(allRegisterStatusTypes ?? []).map((type) => (
                                <Select.Option key={type} value={type}>
                                    {type}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="expiredInsuranceDate"
                        label="Insurance Expired"
                        rules={[{ required: true, message: 'Please select the insurance expiration date!' }]}
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <DatePicker format={'YYYY-MM-DD'} onChange={handleSelectExpiredInsuranceDate} />
                    </Form.Item>
                    <Form.Item
                        name="expiredAccountDate"
                        label="Account Expired"
                        rules={[{ required: true, message: 'Please select the account expiration date!' }]}
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <DatePicker format={'YYYY-MM-DD'} onChange={handleSelectExpiredAccountDate} />
                    </Form.Item>
                    <Form.Item
                        name="workRangeKm"
                        label="Work Range"
                        rules={[{ required: true }]}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 10 }}
                        style={{ display: 'inline-block', width: '80%' }}
                    >
                        <Select onChange={(value, option) => handleRadiusChange(option['key'])}>
                            {WORK_RADIUS_OPTIONS.map((opt) => (
                                <Select.Option key={opt.key} value={opt.key}>
                                    {opt.range} km {opt.note && ` - ${opt.note}`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="monthlyFee"
                        label="Monthly Fee"
                        rules={[{ required: true }]}
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 10 }}
                    >
                        <InputNumber disabled addonAfter="TND" />
                    </Form.Item>
                    <Form.Item
                        name="commissionRate"
                        label="Commission"
                        rules={[{ required: true }]}
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 10 }}
                    >
                        <InputNumber disabled addonAfter="%" />
                    </Form.Item>
                    <Form.Item
                        name="note"
                        label="Note"
                        style={{ display: 'inline-block', width: '80%' }}
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 10 }}
                    >
                        <Input.TextArea rows={4} placeholder="Enter note" />
                    </Form.Item>
                    <Form.Item label="Referral Reward status" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                        <Form.Item name="isRewardedReferral" valuePropName="checked" noStyle>
                            <Switch
                                onChange={handleSwitchChange}
                                checkedChildren="Rewarded"
                                unCheckedChildren="Not Rewarded"
                            />
                        </Form.Item>
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};

export default RegisterTaxiDriverEditModal;
